import { formatTimestampToDateTime, formatISODateTime } from "utils/dateTimeUtils";
import { maskPersonNameForExport, maskInternationalPhoneForExport } from "utils/exportDesensitize";

// 数据映射服务 - 将订单数据映射为Excel导出格式
export class DataMapperService {

    // 时间戳格式化函数
    static formatTimestamp(timestamp: string | number | null | undefined): string {
        if (!timestamp) return '';
        try {
            const date = new Date(typeof timestamp === 'string' ? parseInt(timestamp) * 1000 : timestamp * 1000);
            return date.toLocaleString('zh-CN', { 
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            }).replace(/\//g, '-');
        } catch (error) {
            console.error('时间格式化失败:', error);
            return '';
        }
    }

    // 将订单数据映射为Excel行数据
    static mapOrdersToExcelData(orders: any[]): any[] {
        return orders.map(order => ({
            '运单号': order.trackingNumber || '',
            '客户ID': order.channelId || '',
            '客户名称': order.channel?.channelName || '',
            '客户订单ID': order.channelOrderNumber || '',
            '下单时间': this.formatTimestamp(order.orderTime),
            '揽收入库时间': this.formatTimestamp(order.receivedAtHub),
            '派件扫描时间': this.formatTimestamp(order.lastDeliveryScanTime),
            '运单状态': order.overallStatus || '',
            '是否已送达': order.isDelivered ? '是' : '否',
            '是否被拦截': order.isIntercepted ? '是' : '否',
            '是否异常': order.isAbnormal ? '是' : '否',
            '预计交付时间': this.formatTimestamp(order.expectedDeliveryTime),
            '送达时间': this.formatTimestamp(order.deliveredAt),
            '送达尝试次数': order.deliveryAttempts || 0,
            '异常原因代码': order.exceptionReasonCode || '',
            '发件人地址': order.senderAddress ? `${order.senderAddress.addressLine1 || ''} ${order.senderAddress.addressLine2 || ''} ${order.senderAddress.city || ''} ${order.senderAddress.state || ''} ${order.senderAddress.zipcode || ''}`.trim() : '',
            '发件人电话': (() => {
                const sa = order.senderAddress;
                if (!sa) return '';
                return maskInternationalPhoneForExport(
                    sa.countryCode ?? null,
                    sa.phoneNumber ?? null,
                    sa.extension ?? null
                );
            })(),
            '收件人地址': order.recipientAddress ? `${order.recipientAddress.addressLine1 || ''} ${order.recipientAddress.addressLine2 || ''} ${order.recipientAddress.city || ''} ${order.recipientAddress.state || ''} ${order.recipientAddress.zipcode || ''}`.trim() : '',
            '收件人姓名': maskPersonNameForExport(order.recipientAddress?.fullName || ''),
            '收件人电话': (() => {
                const ra = order.recipientAddress;
                if (!ra) return '';
                return maskInternationalPhoneForExport(
                    ra.countryCode ?? null,
                    ra.phoneNumber ?? null,
                    ra.extension ?? null
                );
            })(),
            '收件人邮编': order.recipientAddress?.zipcode || '',
            '发件地址邮编': order.senderAddress?.zipcode || '',
            '揽收中心': order.pickupHub?.name || '',
            '目的地 State': order.senderAddress?.state || '',
            '目的地 WHS': order.destWarehouse?.name || '',
            '分拣代码(3段码格式)': order.sortingCode || '',
            '重量(磅)': order.originalWeightLb || 0,
            '长度(英寸)': order.originalLengthIn || 0,
            '宽度(英寸)': order.originalWidthIn || 0,
            '高度(英寸)': order.originalHeightIn || 0,
            '实际重量(磅)': order.weightLb || 0,
            '体积重量(磅)': order.volumetricWeightLb || 0,
            '实际长度(英寸)': order.lengthIn || 0,
            '实际宽度(英寸)': order.widthIn || 0,
            '实际高度(英寸)': order.heightIn || 0,
            '包裹价值(美元)': order.packageValueUsd || 0,
            '运费(USD)': order.orderPrice || 0,
            'SLA承诺时间': order.slaTime || '',
            'Zone区': order.zone || '',
            '所属DSP': order.dsp?.name || '',
            '签收司机名称': order.driver?.name || '',
            '签收时间': this.formatTimestamp(order.deliveredAt),
            '最后更新时间': order.updatedAt ? formatISODateTime(order.updatedAt) : ''
        }));
    }

    // Excel列定义
    static getExcelColumns() {
        return [
            { title: '运单号', dataIndex: '运单号' },
            { title: '客户ID', dataIndex: '客户ID' },
            { title: '客户名称', dataIndex: '客户名称' },
            { title: '客户订单ID', dataIndex: '客户订单ID' },
            { title: '下单时间', dataIndex: '下单时间' },
            { title: '揽收入库时间', dataIndex: '揽收入库时间' },
            { title: '派件扫描时间', dataIndex: '派件扫描时间' },
            { title: '运单状态', dataIndex: '运单状态' },
            { title: '是否已送达', dataIndex: '是否已送达' },
            { title: '是否被拦截', dataIndex: '是否被拦截' },
            { title: '是否异常', dataIndex: '是否异常' },
            { title: '预计交付时间', dataIndex: '预计交付时间' },
            { title: '送达时间', dataIndex: '送达时间' },
            { title: '送达尝试次数', dataIndex: '送达尝试次数' },
            { title: '异常原因代码', dataIndex: '异常原因代码' },
            { title: '发件人地址', dataIndex: '发件人地址' },
            { title: '发件人电话', dataIndex: '发件人电话' },
            { title: '发件地址邮编', dataIndex: '发件地址邮编' },
            { title: '收件人地址', dataIndex: '收件人地址' },
            { title: '收件人姓名', dataIndex: '收件人姓名' },
            { title: '收件人电话', dataIndex: '收件人电话' },
            { title: '收件人邮编', dataIndex: '收件人邮编' },
            { title: '揽收中心', dataIndex: '揽收中心' },
            { title: '目的地 State', dataIndex: '目的地 State' },
            { title: '目的地 WHS', dataIndex: '目的地 WHS' },
            { title: '分拣代码(3段码格式)', dataIndex: '分拣代码(3段码格式)' },
            { title: '重量(磅)', dataIndex: '重量(磅)' },
            { title: '体积重量(磅)', dataIndex: '体积重量(磅)' },
            { title: '长度(英寸)', dataIndex: '长度(英寸)' },
            { title: '宽度(英寸)', dataIndex: '宽度(英寸)' },
            { title: '高度(英寸)', dataIndex: '高度(英寸)' },
            { title: '实际重量(磅)', dataIndex: '实际重量(磅)' },
            { title: '实际长度(英寸)', dataIndex: '实际长度(英寸)' },
            { title: '实际宽度(英寸)', dataIndex: '实际宽度(英寸)' },
            { title: '实际高度(英寸)', dataIndex: '实际高度(英寸)' },
            { title: '包裹价值(美元)', dataIndex: '包裹价值(美元)' },
            { title: '运费(USD)', dataIndex: '运费(USD)' },
            { title: 'SLA承诺时间', dataIndex: 'SLA承诺时间' },
            { title: 'Zone区', dataIndex: 'Zone区' },
            { title: '所属DSP', dataIndex: '所属DSP' },
            { title: '签收司机名称', dataIndex: '签收司机名称' },
            { title: '签收时间', dataIndex: '签收时间' },
            { title: '最后更新时间', dataIndex: '最后更新时间' }
        ];
    }

    // 将异常数据映射为Excel行数据
    static mapExceptionsToExcelData(
        exceptions: any[], 
    ): any[] {
        return exceptions.map(exception => {
            return {
                'TRK': exception.trackingNumber || '',
                'Client': exception.channelName || '-',
                'Hub': exception.exceptionHubName || 'N/A',
                'Whs': exception.exceptionWarehouseName || 'N/A',
                'Exception Reason': exception.exceptionReasonText || '-',
                'Exception Time': exception.markExceptionAt ? formatTimestampToDateTime(exception.markExceptionAt) :'-',
                'Parcel Status': exception.overallStatus || '',
                'Delivery Attempts': exception.deliveryAttempts || 0,
                'Receiver Addr': exception.receiverFullAddress || 'N/A',
                'Exception Status': exception.exceptionStatus === 10 ? 'Open' : exception.exceptionStatus === 50 ? 'Processed' : exception.exceptionStatus === 90 ? 'Resolved' : (exception.isAbnormal ? 'Open' : 'Resolved'),
                'Next Action': exception.nextAction === 'None' ? '-' : exception.nextAction
            };
        });
    }

    // Excel列定义
    static getExceptionsExcelColumns() {
        return [
            { title: 'TRK', dataIndex: 'TRK' },
            { title: 'Client', dataIndex: 'Client' },
            { title: 'Hub', dataIndex: 'Hub' },
            { title: 'Whs', dataIndex: 'Whs' },
            { title: 'Exception Reason', dataIndex: 'Exception Reason' },
            { title: 'Exception Time', dataIndex: 'Exception Time' },
            { title: 'Parcel Status', dataIndex: 'Parcel Status' },
            { title: 'Delivery Attempts', dataIndex: 'Delivery Attempts' },
            { title: 'Receiver Addr', dataIndex: 'Receiver Addr' },
            { title: 'Exception Status', dataIndex: 'Exception Status' },
            { title: 'Next Action', dataIndex: 'Next Action' }
        ];
    }

    // 将关单工具订单数据映射为Excel行数据
    static mapOrderCloseToExcelData(orders: any[]): any[] {
        return orders.map(order => {
            // 处理分拣码分段
            const sortingCodeParts = (order.sortingCode || '').split('-');
            const sortingCodeSegment1 = sortingCodeParts[0] || '';
            const sortingCodeSegment2 = sortingCodeParts[1] || '';
            const sortingCodeSegment3 = sortingCodeParts[2] || '';

            // 处理是否有异常
            const hasException = order.isHasBeenSetException ? '是' : '否';

            // 处理异常状态（优先使用三态枚举）
            let exceptionStatus = '-';
            if (order.isHasBeenSetException) {
                if (order.exceptionStatus === 10) exceptionStatus = '新建';
                else if (order.exceptionStatus === 50) exceptionStatus = '已处理';
                else if (order.exceptionStatus === 90) exceptionStatus = '已解决';
                else exceptionStatus = order.isAbnormal ? '未解决' : '已解决';
            }

            // 处理关单记录
            const closeRecord = (order as any).latestCloseRecord;
            // 关单时间格式化：与列表显示保持一致，支持 ISO 8601 格式和时间戳格式
            let closeTime = '';
            if (closeRecord?.closeTime) {
                const closeTimeStr = String(closeRecord.closeTime);
                // 检查是否是 ISO 8601 格式（包含 'T' 或 'Z'）
                if (closeTimeStr.includes('T') || closeTimeStr.includes('Z')) {
                    closeTime = formatISODateTime(closeTimeStr);
                } else {
                    // 否则按时间戳处理
                    closeTime = this.formatTimestamp(closeTimeStr);
                }
            }
            const accountId = closeRecord?.closeOperatorAccountId;
            const accountName = closeRecord?.closeOperatorAccountName;
            const closeOperator = accountId 
                ? (accountName ? `${accountName} (${accountId})` : accountId)
                : '';
            const closeType = closeRecord?.closeType !== undefined ? this.getCloseTypeText(closeRecord.closeType) : '';

            return {
                '运单号': order.trackingNumber || '',
                '订单号': order.channelOrderNumber || '',
                '客户': order.channel?.channelName || order.channel?.clientCode || '',
                '运单状态': order.overallStatus || '',
                '第一分拣码': sortingCodeSegment1,
                '第二分拣码': sortingCodeSegment2,
                '第三分拣码': sortingCodeSegment3,
                'DSP': order.dsp?.name || '',
                '揽收入库时间': this.formatTimestamp(order.receivedAtHub),
                '派送仓入库时间': this.formatTimestamp(order.destWarehouseArrivedAt),
                '最近派送任务时间': this.formatTimestamp(order.lastDeliveryScanTime),
                '是否有异常': hasException,
                '异常状态': exceptionStatus,
                '上次异常原因': order.exceptionReasonCode || '',
                '上次异常日期': this.formatTimestamp(order.markExceptionAt),
                '下一步动作': order.nextAction || '',
                '最后指派司机': order.lastAssignedDriverName || '',
                '距离Ascan时间（天）': order.daysFromAscan !== undefined ? order.daysFromAscan : '',
                '距离闭环倒计时（小时）': order.hoursToClose !== undefined ? order.hoursToClose : '',
                '包裹所属流程': order.flowType === 'REVERSE_UNCLOSED' ? '逆向未闭环' : (order.flowType === 'FORWARD_UNCLOSED' ? '正向未闭环' : ''),
                '关单时间': closeTime,
                '关单人': closeOperator,
                '关单类型': closeType
            };
        });
    }

    // Excel列定义（关单工具）
    static getOrderCloseExcelColumns() {
        return [
            { title: '运单号', dataIndex: '运单号' },
            { title: '订单号', dataIndex: '订单号' },
            { title: '客户', dataIndex: '客户' },
            { title: '运单状态', dataIndex: '运单状态' },
            { title: '第一分拣码', dataIndex: '第一分拣码' },
            { title: '第二分拣码', dataIndex: '第二分拣码' },
            { title: '第三分拣码', dataIndex: '第三分拣码' },
            { title: 'DSP', dataIndex: 'DSP' },
            { title: '揽收入库时间', dataIndex: '揽收入库时间' },
            { title: '派送仓入库时间', dataIndex: '派送仓入库时间' },
            { title: '最近派送任务时间', dataIndex: '最近派送任务时间' },
            { title: '是否有异常', dataIndex: '是否有异常' },
            { title: '异常状态', dataIndex: '异常状态' },
            { title: '上次异常原因', dataIndex: '上次异常原因' },
            { title: '上次异常日期', dataIndex: '上次异常日期' },
            { title: '下一步动作', dataIndex: '下一步动作' },
            { title: '最后指派司机', dataIndex: '最后指派司机' },
            { title: '距离Ascan时间（天）', dataIndex: '距离Ascan时间（天）' },
            { title: '距离闭环倒计时（小时）', dataIndex: '距离闭环倒计时（小时）' },
            { title: '包裹所属流程', dataIndex: '包裹所属流程' },
            { title: '关单时间', dataIndex: '关单时间' },
            { title: '关单人', dataIndex: '关单人' },
            { title: '关单类型', dataIndex: '关单类型' }
        ];
    }

    // 获取关单类型文本
    private static getCloseTypeText(closeType: number): string {
        switch (closeType) {
            case 0:
                return '已丢失';
            case 1:
                return '派送失败';
            case 2:
                return '发起退件';
            case 3:
                return '已退回';
            default:
                return String(closeType);
        }
    }
} 